import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import {
  TrendingUp, DollarSign, Activity, Download, Filter, CalendarDays,
  ArrowUp, ArrowDown, Check, ChevronsUpDown, Save, ChevronDown,
  ChevronRight, AlertCircle, Percent
} from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';

const api = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + '/api' });
api.interceptors.request.use((c) => { const t = localStorage.getItem('token'); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const fmt = (v) => `₹${(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDec = (v) => `₹${(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 })}`;

const FIXED_COST_FIELDS = [
  { key: 'real_estate', label: 'Real Estate (Rent/Lease)' },
  { key: 'human_resources', label: 'Human Resources' },
  { key: 'utilities', label: 'Utilities (Electricity, Water, Internet)' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'marketing', label: 'Marketing & Advertising' },
  { key: 'bank_interest', label: 'Bank Interest' },
  { key: 'others', label: 'Others' },
  { key: 'miscellaneous', label: 'Miscellaneous' },
];

const ProfitBadge = ({ value }) => {
  if (value === undefined || value === null) return null;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${value >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
      {value >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
      {Math.abs(value)}%
    </span>
  );
};

const EbitdaPage = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [excludedServices, setExcludedServices] = useState(new Set());
  const [defaultExcludesApplied, setDefaultExcludesApplied] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [expenseModal, setExpenseModal] = useState(null); // month string
  const [expenseForm, setExpenseForm] = useState({});
  const [consumableModal, setConsumableModal] = useState(null); // month string
  const [consumableCosts, setConsumableCosts] = useState({});
  const [savingExpense, setSavingExpense] = useState(false);
  const [dailyExpanded, setDailyExpanded] = useState(false);
  const [editingCost, setEditingCost] = useState(null); // appointment_id being edited
  const [editCostValue, setEditCostValue] = useState('');

  useEffect(() => {
    const now = new Date();
    setEndDate(now.toISOString().split('T')[0]);
    const start = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    setStartDate(start.toISOString().split('T')[0]);
  }, []);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const startMonth = startDate.slice(0, 7);
      const endMonth = endDate.slice(0, 7);
      const params = new URLSearchParams({ start_month: startMonth, end_month: endMonth });
      if (excludedServices.size > 0) params.append('exclude_services', [...excludedServices].join(','));
      const res = await api.get(`/analytics/ebitda?${params}`);
      setData(res.data);

      // Apply default exclusions on first load
      if (!defaultExcludesApplied && res.data.services_without_jc?.length > 0) {
        setExcludedServices(new Set(res.data.services_without_jc));
        setDefaultExcludesApplied(true);
      }
    } catch (err) {
      toast.error('Failed to fetch EBITDA data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, excludedServices, defaultExcludesApplied]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openExpenseModal = async (month) => {
    try {
      const res = await api.get(`/expenses/monthly/${month}`);
      setExpenseForm(res.data || {});
    } catch { setExpenseForm({}); }
    setExpenseModal(month);
  };

  const saveExpense = async () => {
    setSavingExpense(true);
    try {
      await api.post('/expenses/monthly', { ...expenseForm, month: expenseModal });
      toast.success(`Expenses saved for ${expenseModal}`);
      setExpenseModal(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to save expenses');
    } finally {
      setSavingExpense(false);
    }
  };

  const openConsumableModal = async (month) => {
    try {
      const res = await api.get(`/expenses/consumable/${month}`);
      const map = {};
      res.data.forEach(c => { map[c.service_id] = c.cost_per_procedure || 0; });
      setConsumableCosts(map);
    } catch { setConsumableCosts({}); }
    setConsumableModal(month);
  };

  const saveConsumable = async (serviceId, serviceName, costValue) => {
    try {
      await api.post('/expenses/consumable', {
        month: consumableModal, service_id: serviceId,
        service_name: serviceName, cost_per_procedure: parseFloat(costValue) || 0
      });
    } catch { toast.error('Failed to save'); }
  };

  const saveAllConsumables = async () => {
    setSavingExpense(true);
    const services = data?.services_list?.filter(s => !excludedServices.has(s.id)) || [];
    for (const s of services) {
      const cost = consumableCosts[s.id] || 0;
      await saveConsumable(s.id, s.name, cost);
    }
    toast.success(`Consumable costs saved for ${consumableModal}`);
    setConsumableModal(null);
    setSavingExpense(false);
    fetchData();
  };

  const saveAppointmentConsumable = async (aptId, cost) => {
    try {
      await api.post('/expenses/appointment-consumable', {
        appointment_id: aptId,
        consumable_cost: parseFloat(cost) || 0
      });
      toast.success('Consumable cost updated');
      setEditingCost(null);
      fetchData();
    } catch {
      toast.error('Failed to save');
    }
  };

  const handleExportCSV = () => {
    if (!data) return;
    let csv = 'Month,Revenue,Fixed Costs,Variable Costs,Total Cost,EBITDA,Margin %,Procedures\n';
    data.monthly_ebitda?.forEach(m => {
      csv += `${m.month_name},${m.revenue},${m.fixed_costs},${m.variable_costs},${m.total_cost},${m.ebitda},${m.margin}%,${m.total_procedures}\n`;
    });
    csv += '\nService Profitability (Latest Month)\n';
    const latest = data.monthly_ebitda?.findLast(m => m.per_service?.length > 0);
    if (latest) {
      csv += 'Service,Procedures,Revenue,Fixed Alloc,Consumable,Total Cost,Profit,Margin %,Avg Rev,Avg Cost,Avg Profit\n';
      latest.per_service.forEach(s => {
        csv += `${s.service_name},${s.procedures},${s.revenue},${s.allocated_fixed},${s.consumable_cost},${s.total_cost},${s.profit},${s.margin}%,${s.avg_revenue},${s.avg_cost},${s.avg_profit}\n`;
      });
    }
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ebitda-${startDate.slice(0, 7)}-to-${endDate.slice(0, 7)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const gt = data?.grand_totals || {};

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-gray-800 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</p>
        ))}
      </div>
    );
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 space-y-6 bg-gray-50 min-h-screen" data-testid="ebitda-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="page-title">EBITDA Report</h1>
            <p className="text-sm text-gray-500 mt-1">Monthly profitability & per-procedure costing analysis</p>
          </div>
          <Button onClick={handleExportCSV} variant="outline" className="gap-2" disabled={!data} data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card data-testid="filters-card">
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-gray-500" />
                <div>
                  <label className="text-xs text-gray-500 block">From</label>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border rounded-md px-3 py-1.5 text-sm" data-testid="start-date" />
                </div>
                <span className="text-gray-400 mt-4">—</span>
                <div>
                  <label className="text-xs text-gray-500 block">To</label>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border rounded-md px-3 py-1.5 text-sm" data-testid="end-date" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <div>
                  <label className="text-xs text-gray-500 block">Include/Exclude Services</label>
                  <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[280px] justify-between text-sm font-normal" data-testid="service-filter">
                        {excludedServices.size === 0
                          ? 'All Services Included'
                          : `${excludedServices.size} excluded`}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[280px] p-0" align="start">
                      <div className="p-2 border-b flex gap-2">
                        <Button variant="ghost" size="sm" className="text-xs" onClick={() => setExcludedServices(new Set())}>Include All</Button>
                        <Button variant="ghost" size="sm" className="text-xs" onClick={() => setExcludedServices(new Set(data?.services_without_jc || []))}>Exclude No-JC</Button>
                      </div>
                      <div className="max-h-[240px] overflow-y-auto p-1">
                        {data?.services_list?.map(s => {
                          const included = !excludedServices.has(s.id);
                          return (
                            <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 cursor-pointer text-sm">
                              <Checkbox
                                checked={included}
                                onCheckedChange={(val) => {
                                  setExcludedServices(prev => {
                                    const next = new Set(prev);
                                    val ? next.delete(s.id) : next.add(s.id);
                                    return next;
                                  });
                                }}
                              />
                              <span className={included ? '' : 'text-gray-400 line-through'}>{s.name}</span>
                              {!s.has_job_card && <Badge variant="outline" className="text-xs ml-auto">No JC</Badge>}
                            </label>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-spinner">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
          </div>
        ) : data ? (
          <>
            {/* Grand Totals */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="grand-totals">
              <Card className="border-l-4 border-l-emerald-500">
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Total Revenue</p>
                  <p className="text-2xl font-bold text-emerald-700" data-testid="total-revenue">{fmt(gt.revenue)}</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-red-400">
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Total Costs</p>
                  <p className="text-2xl font-bold text-red-600">{fmt(gt.total_cost)}</p>
                </CardContent>
              </Card>
              <Card className={`border-l-4 ${gt.ebitda >= 0 ? 'border-l-emerald-600' : 'border-l-red-600'}`}>
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">EBITDA</p>
                  <p className={`text-2xl font-bold ${gt.ebitda >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="total-ebitda">{fmt(gt.ebitda)}</p>
                  <ProfitBadge value={gt.margin} />
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-amber-500">
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Avg Rev / Procedure</p>
                  <p className="text-xl font-bold text-amber-700">{fmtDec(gt.avg_revenue_per_proc)}</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-indigo-500">
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Avg Profit / Procedure</p>
                  <p className={`text-xl font-bold ${gt.avg_profit_per_proc >= 0 ? 'text-indigo-700' : 'text-red-700'}`}>{fmtDec(gt.avg_profit_per_proc)}</p>
                </CardContent>
              </Card>
            </div>

            {/* EBITDA Trend Chart */}
            <Card data-testid="ebitda-trend-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-500" /> EBITDA Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.trend?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month_short" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <ReferenceLine y={0} stroke="#999" />
                      <Bar dataKey="revenue" name="Revenue" fill="#10b981" radius={[4,4,0,0]} />
                      <Bar dataKey="costs" name="Costs" fill="#ef4444" radius={[4,4,0,0]} />
                      <Bar dataKey="ebitda" name="EBITDA" fill="#6366f1" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-gray-400 text-center py-10">No data</p>}
              </CardContent>
            </Card>

            {/* Margin Trend */}
            <Card data-testid="margin-trend-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Percent className="w-4 h-4 text-indigo-500" /> EBITDA Margin Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.trend?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={data.trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month_short" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} domain={['auto', 'auto']} />
                      <Tooltip formatter={(v) => `${v}%`} />
                      <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="5 5" />
                      <Line type="monotone" dataKey="margin" name="Margin %" stroke="#6366f1" strokeWidth={2} dot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <p className="text-gray-400 text-center py-10">No data</p>}
              </CardContent>
            </Card>

            {/* Monthly EBITDA Statements */}
            <Card data-testid="monthly-ebitda">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-indigo-500" /> Monthly EBITDA Statements
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.monthly_ebitda?.length > 0 ? (
                  [...data.monthly_ebitda].reverse().map((month) => (
                    <div key={month.month} className="border rounded-lg overflow-hidden" data-testid={`ebitda-month-${month.month}`}>
                      <button
                        onClick={() => setExpandedMonth(expandedMonth === month.month ? null : month.month)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-gray-800">{month.month_name}</span>
                          {month.is_current_month && <Badge className="bg-blue-100 text-blue-700 text-xs">Current</Badge>}
                          {!month.has_expenses && <Badge variant="outline" className="text-xs text-orange-500 border-orange-300">No expenses entered</Badge>}
                        </div>
                        <div className="flex items-center gap-4 text-xs">
                          <span className="text-emerald-600">Rev: {fmt(month.revenue)}</span>
                          <span className="text-red-500">Cost: {fmt(month.total_cost)}</span>
                          <span className={`font-semibold ${month.ebitda >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                            EBITDA: {fmt(month.ebitda)} ({month.margin}%)
                          </span>
                          <span className="text-gray-500">{month.total_procedures} procs</span>
                          {expandedMonth === month.month ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                        </div>
                      </button>
                      {expandedMonth === month.month && (
                        <div className="px-4 pb-4 bg-gray-50 border-t space-y-4">
                          {/* Pro-rata banner */}
                          {month.is_current_month && month.pro_rata?.days_elapsed && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                              <p className="font-medium text-blue-800">Pro-rata Projection ({month.pro_rata.days_elapsed}/{month.pro_rata.days_in_month} days)</p>
                              <div className="flex gap-6 mt-1 text-blue-700">
                                <span>Projected Revenue: {fmt(month.pro_rata.projected_revenue)}</span>
                                <span>Projected EBITDA: {fmt(month.pro_rata.projected_ebitda)}</span>
                                <span>Projected Margin: {month.pro_rata.projected_margin}%</span>
                              </div>
                            </div>
                          )}

                          {/* Action buttons */}
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openExpenseModal(month.month)} data-testid={`edit-expenses-${month.month}`}>
                              <DollarSign className="w-4 h-4 mr-1" /> Edit Fixed Costs
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openConsumableModal(month.month)} data-testid={`edit-consumables-${month.month}`}>
                              <Activity className="w-4 h-4 mr-1" /> Edit Consumable Costs
                            </Button>
                          </div>

                          {/* Cost Breakdown */}
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                            {FIXED_COST_FIELDS.map(f => (
                              <div key={f.key} className="bg-white rounded p-2 border">
                                <p className="text-xs text-gray-500">{f.label}</p>
                                <p className="font-semibold text-gray-800">{fmt(month.cost_breakdown?.[f.key])}</p>
                              </div>
                            ))}
                          </div>

                          {/* EBITDA summary bar */}
                          <div className="bg-white rounded-lg border p-3">
                            <div className="grid grid-cols-5 gap-4 text-center">
                              <div><p className="text-xs text-gray-500">Revenue</p><p className="font-bold text-emerald-600">{fmt(month.revenue)}</p></div>
                              <div><p className="text-xs text-gray-500">Fixed Costs</p><p className="font-bold text-red-500">{fmt(month.fixed_costs)}</p></div>
                              <div><p className="text-xs text-gray-500">Variable Costs</p><p className="font-bold text-orange-500">{fmt(month.variable_costs)}</p></div>
                              <div><p className="text-xs text-gray-500">Total Cost</p><p className="font-bold text-red-600">{fmt(month.total_cost)}</p></div>
                              <div><p className="text-xs text-gray-500">EBITDA</p><p className={`font-bold ${month.ebitda >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(month.ebitda)} ({month.margin}%)</p></div>
                            </div>
                          </div>

                          {/* Per-Service Profitability */}
                          {month.per_service?.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-sm text-gray-700 mb-2">Per-Service Profitability</h4>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b text-gray-500">
                                      <th className="text-left py-2 px-2">Service</th>
                                      <th className="text-right py-2 px-2">Procs</th>
                                      <th className="text-right py-2 px-2 text-emerald-600">Revenue</th>
                                      <th className="text-right py-2 px-2 text-red-400">Fixed Alloc</th>
                                      <th className="text-right py-2 px-2 text-orange-400">Consumable</th>
                                      <th className="text-right py-2 px-2 text-red-600">Total Cost</th>
                                      <th className="text-right py-2 px-2 font-semibold">Profit</th>
                                      <th className="text-right py-2 px-2">Margin</th>
                                      <th className="text-right py-2 px-2 text-amber-600">Avg Rev</th>
                                      <th className="text-right py-2 px-2 text-red-500">Avg Cost</th>
                                      <th className="text-right py-2 px-2 text-indigo-600">Avg Profit</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {month.per_service.map((s, i) => (
                                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="py-2 px-2 font-medium text-gray-800">{s.service_name}</td>
                                        <td className="py-2 px-2 text-right">{s.procedures}</td>
                                        <td className="py-2 px-2 text-right text-emerald-600">{fmt(s.revenue)}</td>
                                        <td className="py-2 px-2 text-right text-red-400">{fmt(s.allocated_fixed)}</td>
                                        <td className="py-2 px-2 text-right text-orange-500">{fmt(s.consumable_cost)}</td>
                                        <td className="py-2 px-2 text-right text-red-600">{fmt(s.total_cost)}</td>
                                        <td className={`py-2 px-2 text-right font-semibold ${s.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(s.profit)}</td>
                                        <td className="py-2 px-2 text-right"><ProfitBadge value={s.margin} /></td>
                                        <td className="py-2 px-2 text-right text-amber-600">{fmtDec(s.avg_revenue)}</td>
                                        <td className="py-2 px-2 text-right text-red-500">{fmtDec(s.avg_cost)}</td>
                                        <td className={`py-2 px-2 text-right font-medium ${s.avg_profit >= 0 ? 'text-indigo-600' : 'text-red-600'}`}>{fmtDec(s.avg_profit)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : <p className="text-gray-400 text-center py-8">No monthly data</p>}
              </CardContent>
            </Card>

            {/* Daily Procedure Profitability */}
            <Card data-testid="daily-procedures">
              <CardHeader className="pb-2">
                <button onClick={() => setDailyExpanded(!dailyExpanded)} className="flex items-center gap-2 w-full text-left">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Activity className="w-4 h-4 text-indigo-500" />
                    Daily Procedure Profitability
                    {data.daily_procedures_month && <Badge className="bg-gray-100 text-gray-600 text-xs ml-2">{data.daily_procedures_month}</Badge>}
                  </CardTitle>
                  {dailyExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 ml-auto" /> : <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />}
                </button>
              </CardHeader>
              {dailyExpanded && (
                <CardContent>
                  {data.daily_procedures?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-gray-500">
                            <th className="text-left py-2 px-2">Date</th>
                            <th className="text-left py-2 px-2">Patient</th>
                            <th className="text-left py-2 px-2">Service</th>
                            <th className="text-right py-2 px-2 text-emerald-600">Revenue</th>
                            <th className="text-right py-2 px-2 text-red-400">Fixed Alloc</th>
                            <th className="text-right py-2 px-2 text-orange-400">Consumable</th>
                            <th className="text-right py-2 px-2 text-red-600">Total Cost</th>
                            <th className="text-right py-2 px-2 font-semibold">Profit</th>
                            <th className="text-center py-2 px-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.daily_procedures.map((p, i) => (
                            <tr key={i} className={`border-b border-gray-100 ${p.profitable ? '' : 'bg-red-50'}`}>
                              <td className="py-2 px-2 text-gray-600">{p.date}</td>
                              <td className="py-2 px-2 font-medium text-gray-800">{p.patient_name}</td>
                              <td className="py-2 px-2 text-gray-700">{p.service_name}</td>
                              <td className="py-2 px-2 text-right text-emerald-600">{fmt(p.revenue)}</td>
                              <td className="py-2 px-2 text-right text-red-400">{fmtDec(p.fixed_cost_alloc)}</td>
                              <td className="py-2 px-2 text-right">
                                {editingCost === p.appointment_id ? (
                                  <div className="flex items-center justify-end gap-1">
                                    <span className="text-xs text-gray-400">₹</span>
                                    <Input
                                      type="number"
                                      className="w-20 h-7 text-right text-sm p-1"
                                      value={editCostValue}
                                      onChange={e => setEditCostValue(e.target.value)}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') saveAppointmentConsumable(p.appointment_id, editCostValue);
                                        if (e.key === 'Escape') setEditingCost(null);
                                      }}
                                      autoFocus
                                      data-testid={`edit-consumable-input-${p.appointment_id}`}
                                    />
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => saveAppointmentConsumable(p.appointment_id, editCostValue)} data-testid={`save-consumable-${p.appointment_id}`}>
                                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                                    </Button>
                                  </div>
                                ) : (
                                  <button
                                    className={`text-right cursor-pointer hover:underline ${p.has_override ? 'text-orange-600 font-semibold' : 'text-orange-400'}`}
                                    onClick={() => { setEditingCost(p.appointment_id); setEditCostValue(p.consumable_cost || 0); }}
                                    title={p.has_override ? `Overridden (default: ${fmtDec(p.default_consumable_cost)})` : 'Click to override'}
                                    data-testid={`consumable-cell-${p.appointment_id}`}
                                  >
                                    {fmtDec(p.consumable_cost)}
                                    {p.has_override && <span className="ml-1 text-xs">*</span>}
                                  </button>
                                )}
                              </td>
                              <td className="py-2 px-2 text-right text-red-600">{fmtDec(p.total_cost)}</td>
                              <td className={`py-2 px-2 text-right font-semibold ${p.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtDec(p.profit)}</td>
                              <td className="py-2 px-2 text-center">
                                {p.profitable
                                  ? <Badge className="bg-emerald-100 text-emerald-700 text-xs">Profitable</Badge>
                                  : <Badge className="bg-red-100 text-red-700 text-xs">Loss</Badge>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="text-xs text-gray-400 mt-2">Click any consumable cost to override. * = custom override (hover to see default).</p>
                    </div>
                  ) : <p className="text-gray-400 text-center py-6">No procedure data for latest month</p>}
                </CardContent>
              )}
            </Card>
          </>
        ) : <div className="text-center py-20 text-gray-400">No data available</div>}
      </div>

      {/* Fixed Expense Modal */}
      <Dialog open={!!expenseModal} onOpenChange={(o) => !o && setExpenseModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Monthly Fixed Costs — {expenseModal}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {FIXED_COST_FIELDS.map(f => (
              <div key={f.key} className="flex items-center justify-between gap-4">
                <Label className="text-sm text-gray-700 min-w-[160px]">{f.label}</Label>
                <Input
                  type="number" placeholder="0"
                  value={expenseForm[f.key] || ''}
                  onChange={e => setExpenseForm(prev => ({ ...prev, [f.key]: parseFloat(e.target.value) || 0 }))}
                  className="w-32 text-right"
                  data-testid={`expense-${f.key}`}
                />
              </div>
            ))}
            <div className="border-t pt-2">
              <Label className="text-sm text-gray-500">Notes</Label>
              <Input
                value={expenseForm.notes || ''}
                onChange={e => setExpenseForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenseModal(null)}>Cancel</Button>
            <Button onClick={saveExpense} disabled={savingExpense} data-testid="save-expenses-btn">
              <Save className="w-4 h-4 mr-1" /> {savingExpense ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Consumable Cost Modal */}
      <Dialog open={!!consumableModal} onOpenChange={(o) => !o && setConsumableModal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Consumable Cost Per Procedure — {consumableModal}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {data?.services_list?.filter(s => !excludedServices.has(s.id)).map(s => (
              <div key={s.id} className="flex items-center justify-between gap-4">
                <Label className="text-sm text-gray-700 flex-1">{s.name}</Label>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-400">₹</span>
                  <Input
                    type="number" placeholder="0" className="w-28 text-right"
                    value={consumableCosts[s.id] || ''}
                    onChange={e => setConsumableCosts(prev => ({ ...prev, [s.id]: parseFloat(e.target.value) || 0 }))}
                    data-testid={`consumable-${s.id}`}
                  />
                  <span className="text-xs text-gray-400">/proc</span>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConsumableModal(null)}>Cancel</Button>
            <Button onClick={saveAllConsumables} disabled={savingExpense} data-testid="save-consumables-btn">
              <Save className="w-4 h-4 mr-1" /> {savingExpense ? 'Saving...' : 'Save All'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default EbitdaPage;
